const CHANNEL_NAME = "session_channel";
const bc = new BroadcastChannel(CHANNEL_NAME);
const TEMP_JWT_KEY = "temp_jwtToken";
const TEMP_REFRESH_KEY = "temp_refreshToken";
const TEMP_PING_KEY = "temp_session_ping";
const TEMP_PING_TTL_MS = 15000;

export type SessionData = {
  jwtToken: string;
  refreshToken: string | null;
  remember: boolean;
  subcontractorId?: string | null;
  slugName?: string | null;
  dataCall?: string | null;
};

class SessionManager {
  private session: SessionData | null = null;
  private subscribers: ((session: SessionData | null) => void)[] = [];
  private tempPingIntervalId: number | null = null;

  constructor() {
    // Charger depuis sessionStorage ou localStorage (si rememberMe)
    const token =
      sessionStorage.getItem("jwtToken") || localStorage.getItem("jwtToken");
    const refresh =
      sessionStorage.getItem("refreshToken") ||
      localStorage.getItem("refreshToken");

    const subcontractorId = sessionStorage.getItem("subcontractorId");
    const slugName = sessionStorage.getItem("slugName");
    const dataCall = sessionStorage.getItem("dataCall");

    if (token && refresh) {
      // On peut stocker un flag "remember" pour savoir si c'était permanent
      const remember = !!localStorage.getItem("jwtToken");
      this.session = {
        jwtToken: token,
        refreshToken: refresh,
        remember,
        subcontractorId,
        slugName,
        dataCall,
      };
    }

    // --- Synchronisation cross-onglets ---
    bc.onmessage = (event) => {
      const { type, data } = event.data;
      switch (type) {
        case "LOGIN":
          this.session = data;
          if (data.remember) {
            localStorage.setItem("jwtToken", data.jwtToken);
            localStorage.setItem("refreshToken", data.refreshToken);
          } else {
            sessionStorage.setItem("jwtToken", data.jwtToken);
            sessionStorage.setItem("refreshToken", data.refreshToken);
          }

          // 🔥 Ces données vont toujours dans sessionStorage
          if (data.subcontractorId)
            sessionStorage.setItem("subcontractorId", data.subcontractorId);
          if (data.slugName) sessionStorage.setItem("slugName", data.slugName);
          if (data.dataCall) sessionStorage.setItem("dataCall", data.dataCall);

          this.notify();
          break;

        case "LOGOUT":
          this.clear();
          break;

        case "SYNC_REQUEST":
          if (this.session) {
            bc.postMessage({ type: "SYNC_RESPONSE", data: this.session });
          }
          break;

        case "SYNC_RESPONSE":
          if (!this.session && data?.jwtToken) {
            this.session = data;
            if (data.remember) {
              localStorage.setItem("jwtToken", data.jwtToken);
              localStorage.setItem("refreshToken", data.refreshToken);
            } else {
              sessionStorage.setItem("jwtToken", data.jwtToken);
              sessionStorage.setItem("refreshToken", data.refreshToken);
            }

            if (data.subcontractorId)
              sessionStorage.setItem("subcontractorId", data.subcontractorId);
            if (data.slugName)
              sessionStorage.setItem("slugName", data.slugName);
            if (data.dataCall)
              sessionStorage.setItem("dataCall", data.dataCall);

            this.notify();
          }
          break;
      }
    };

    if (this.session) {
      if (this.session.remember) {
        this.stopTempPing();
        this.clearTempSession();
      } else {
        this.setTempSession(this.session.jwtToken, this.session.refreshToken);
        this.startTempPing();
      }
    }

    // au démarrage, demander une synchro aux autres onglets
    bc.postMessage({ type: "SYNC_REQUEST" });
  }

  private notify() {
    this.subscribers.forEach((cb) => cb(this.session));
  }

  login(data: SessionData & { remember?: boolean }) {
    this.session = data;

    const rememberMe = data.remember || false; // utiliser le flag passé dans data

    if (rememberMe) {
      localStorage.setItem("jwtToken", data.jwtToken);
      localStorage.setItem("refreshToken", data.refreshToken);
    } else {
      sessionStorage.setItem("jwtToken", data.jwtToken);
      sessionStorage.setItem("refreshToken", data.refreshToken);
    }

    if (data.subcontractorId)
      sessionStorage.setItem("subcontractorId", data.subcontractorId);
    if (data.slugName) sessionStorage.setItem("slugName", data.slugName);
    if (data.dataCall) sessionStorage.setItem("dataCall", data.dataCall);

    bc.postMessage({ type: "LOGIN", data });
    this.notify();
  }

  logout() {
    this.clear();
    bc.postMessage({ type: "LOGOUT" });
  }

  private clear() {
    this.session = null;
    sessionStorage.removeItem("jwtToken");
    sessionStorage.removeItem("refreshToken");
    localStorage.removeItem("jwtToken");
    localStorage.removeItem("refreshToken");
    this.stopTempPing();
    this.clearTempSession();
    sessionStorage.removeItem("subcontractorId");
    sessionStorage.removeItem("slugName");
    sessionStorage.removeItem("dataCall");
    this.notify();
  }

  getToken() {
    return this.session?.jwtToken || null;
  }

  getRefreshToken() {
    return this.session?.refreshToken || null;
  }

  subscribe(cb: (session: SessionData | null) => void) {
    this.subscribers.push(cb);
    return () => {
      this.subscribers = this.subscribers.filter((s) => s !== cb);
    };
  }

  // --- Méthodes pour gérer les données spécifiques sessionStorage ---
  setSubcontractorId(id: string) {
    sessionStorage.setItem("subcontractorId", id);
  }

  getSubcontractorId(): string | null {
    return sessionStorage.getItem("subcontractorId");
  }

  setDataCall(call: any) {
    sessionStorage.setItem("dataCall", JSON.stringify(call));
  }

  getDataCall(): any | null {
    const data = sessionStorage.getItem("dataCall");
    return data ? JSON.parse(data) : null;
  }

  setSlugName(slug: string) {
    sessionStorage.setItem("slugName", slug);
  }

  getSlugName(): string | null {
    return sessionStorage.getItem("slugName");
  }

  private setTempSession(jwtToken: string, refreshToken: string) {
    localStorage.setItem(TEMP_JWT_KEY, jwtToken);
    localStorage.setItem(TEMP_REFRESH_KEY, refreshToken);
    localStorage.setItem(TEMP_PING_KEY, Date.now().toString());
  }

  private clearTempSession() {
    localStorage.removeItem(TEMP_JWT_KEY);
    localStorage.removeItem(TEMP_REFRESH_KEY);
    localStorage.removeItem(TEMP_PING_KEY);
  }

  private startTempPing() {
    if (this.tempPingIntervalId !== null) return;
    this.tempPingIntervalId = window.setInterval(() => {
      localStorage.setItem(TEMP_PING_KEY, Date.now().toString());
    }, TEMP_PING_TTL_MS / 3);
  }

  private stopTempPing() {
    if (this.tempPingIntervalId === null) return;
    window.clearInterval(this.tempPingIntervalId);
    this.tempPingIntervalId = null;
  }
}

export const sessionManager = new SessionManager();
